'use client';

import { useEffect, useRef } from 'react';

/**
 * Initializes Lenis smooth scrolling on mount.
 * Respects prefers-reduced-motion. Cleans up on unmount.
 * Deferred to requestIdleCallback so it never blocks initial paint or LCP.
 */
export function useLenis(enabled = true) {
  const lenisRef = useRef<InstanceType<typeof import('lenis').default> | null>(null);

  useEffect(() => {
    if (!enabled) return;
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) return;

    let raf = 0;
    let cancelled = false;
    let cancelIdle: (() => void) | null = null;

    const init = () => {
      if (cancelled) return;
      void import('lenis').then(({ default: Lenis }) => {
        if (cancelled) return;
        const lenis = new Lenis({
          duration: 1.2,
          easing: (t: number) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
          touchMultiplier: 2,
          infinite: false,
        });

        lenisRef.current = lenis;

        const tick = (time: number) => {
          lenis.raf(time);
          raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
      });
    };

    if (typeof window.requestIdleCallback === 'function') {
      const handle = window.requestIdleCallback(init, { timeout: 2000 });
      cancelIdle = () => window.cancelIdleCallback?.(handle);
    } else {
      const t = window.setTimeout(init, 1500);
      cancelIdle = () => window.clearTimeout(t);
    }

    return () => {
      cancelled = true;
      cancelIdle?.();
      cancelAnimationFrame(raf);
      lenisRef.current?.destroy();
      lenisRef.current = null;
    };
  }, [enabled]);

  return lenisRef;
}
